import { ref } from 'vue';

/**
 * Player app updates without a reboot (D14, R5).
 *
 * A screen nobody touches never navigates, so the browser's own service-worker
 * update check — which rides on navigations — never runs. This module asks for
 * one every ~30 minutes. The worker (`sw.ts`) calls `skipWaiting()` and
 * `clientsClaim()` unconditionally, so a new build takes control of the page
 * the moment it installs; the *page*, however, is still running the old bundle
 * (trap T3). The signal is therefore `controllerchange`, and the response is a
 * reload at the next item boundary, behind black, so nothing visibly cuts.
 *
 * Not workbox-window's `controlling` event: its `isUpdate` is a snapshot of
 * whether a worker controlled the page when `register()` ran. On a freshly
 * installed Pi the first worker installs during this very page's life, so every
 * later update would arrive as `isUpdate: false` and the screen would never
 * update until its next reboot. Tracking the controller across changes gets
 * both cases right: from none to one is the first install, from one to another
 * is an update.
 */

export const UPDATE_CHECK_BASE_MS = 30 * 60_000;
export const UPDATE_CHECK_JITTER_MS = 5 * 60_000;
/** R5: reload only on a network known good this recently. */
export const NETWORK_FRESH_MS = 60_000;
/** D14: an update waits for a boundary at most this long. */
export const RELOAD_CAP_MS = 15 * 60_000;
export const COVER_FADE_MS = 300;

/** Shown by `App.vue` over everything while a reload is under way. */
export const coverVisible = ref(false);

let pendingSince: number | null = null;

export function updatePending(): boolean {
  return pendingSince !== null;
}

/** For tests. */
export function resetUpdateState(): void {
  pendingSince = null;
  coverVisible.value = false;
}

export interface ReloadDecisionInput {
  /** When the new build took control, or null if none has. */
  pendingSince: number | null;
  /** True when called at an item boundary, false from the cap timer. */
  atBoundary: boolean;
  /** A signal override (interval, emergency) is on the glass. */
  overrideOnAir: boolean;
  /** Last confirmed API round trip, epoch ms. */
  lastNetworkOkAt: number | null;
  now: number;
}

/**
 * Pure. R5's two vetoes come first and apply to the cap as well: the timeline
 * cache key carries `from`/`to` (F2.7), so a reload without the network may
 * come back to the idle brand mark — and an evacuation notice is never worth
 * interrupting for a build.
 */
export function shouldReload(input: ReloadDecisionInput): boolean {
  if (input.pendingSince === null) return false;
  if (input.overrideOnAir) return false;
  if (input.lastNetworkOkAt === null || input.now - input.lastNetworkOkAt > NETWORK_FRESH_MS) {
    return false;
  }
  if (input.atBoundary) return true;
  // An empty playlist and the idle brand mark have no boundaries at all (F2.6).
  return input.now - input.pendingSince >= RELOAD_CAP_MS;
}

/** `shouldReload` against this page's update state. */
export function shouldReloadNow(input: Omit<ReloadDecisionInput, 'pendingSince'>): boolean {
  return shouldReload({ ...input, pendingSince });
}

export function nextCheckDelayMs(random: () => number = Math.random): number {
  return UPDATE_CHECK_BASE_MS + Math.round((random() * 2 - 1) * UPDATE_CHECK_JITTER_MS);
}

/**
 * Black the screen, give the fade a frame and its duration, then reload. Used
 * by update reloads and by the remote Reload command alike, so an operator's
 * reload looks the same as a deploy's.
 */
export async function reloadBehindCover(
  reload: () => void = () => location.reload(),
): Promise<void> {
  // The cap timer and a boundary can both decide to reload inside one fade.
  if (coverVisible.value) return;
  coverVisible.value = true;
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => setTimeout(resolve, COVER_FADE_MS));
  reload();
}

type Container = Pick<ServiceWorkerContainer, 'register' | 'addEventListener'> & {
  readonly controller: ServiceWorker | null;
};

export interface UpdateCheckDeps {
  container?: Container;
  now?: () => number;
  random?: () => number;
  swUrl?: string;
}

/**
 * Registers the worker (the plugin's own `registerSW.js` is off — trap T3) and
 * starts the jittered update checks. Production builds only: the dev server
 * serves no worker (`devOptions.enabled: false`).
 */
export function startUpdateChecks(deps: UpdateCheckDeps = {}): () => void {
  const container =
    deps.container ?? (typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined);
  if (!container) return () => undefined;
  const now = deps.now ?? Date.now;
  const random = deps.random ?? Math.random;

  let controller = container.controller;
  container.addEventListener('controllerchange', () => {
    const previous = controller;
    controller = container.controller;
    if (previous !== null && pendingSince === null) pendingSince = now();
  });

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  void container
    .register(deps.swUrl ?? '/sw.js')
    .then((registration) => {
      const check = (): void => {
        if (stopped) return;
        timer = setTimeout(() => {
          // A failed check (offline, 5xx) is simply retried next time.
          registration.update().catch(() => undefined);
          check();
        }, nextCheckDelayMs(random));
      };
      check();
    })
    .catch(() => undefined);

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
